import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { OrderItemRow } from '@models/order/orderLog';
import { useTranslation } from 'react-i18next';

interface Props {
    orderItem: OrderItemRow;
}
function OrderItem({ orderItem }:Props) {
    const { t } = useTranslation('orderLog');
    const {
        name, salePrice, thumbnailUrl, qty, orderOptions,
    } = orderItem;
    const optionAmount = orderOptions
        .map((option) => option.qty * option.salePrice)
        .reduce((prev, curr) => prev + curr, 0);
    const totalAmount = (salePrice + optionAmount) * qty;
    const priceUnit = usePriceUnit();
    return (
        <Row fullWidth spacing={6} align='center'>
            <ImageWrapper>
                <Thumbnail src={thumbnailUrl}
                    width={100}
                    height={100}
                    alt={t('item.imageAlt')}/>
            </ImageWrapper>
            <TextContainer spacing={2}>
                <NameText size='title' fontWeight='medium'>
                    {name}
                </NameText>
                {orderOptions.length > 0 && (
                    <Column spacing={1}>
                        {[...orderOptions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((option) => (
                                <Text size='body-long'
                                    color='var(--color-text-secondary)'
                                    key={option.optionId}>
                                    {option.name}{t('item.optionPrice', { price: `${(option.qty * option.salePrice).toLocaleString()}${priceUnit}` })}
                                </Text>
                            ))}
                    </Column>
                )}
                <QuantityRow fullWidth justify='space-between'>
                    <Text fontWeight='medium' color='var(--color-text-secondary)'>
                        {t('item.quantity', { qty: qty.toLocaleString() })}
                    </Text>
                    <Text fontWeight='medium'>
                        {totalAmount.toLocaleString()}{priceUnit}
                    </Text>
                </QuantityRow>
            </TextContainer>
        </Row>
    );
}

const ImageWrapper = styled.div`
    border-radius: var(--radius-xs);
    flex-shrink: 0;
    height: 100px;
    overflow: hidden;
    width: 100px;
`;

const Thumbnail = styled(Image)`
    height: 100%;
    object-fit: cover;
    width: 100%;
`;

const TextContainer = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const NameText = styled(Text)`
    white-space: pre-wrap;
    word-break: break-all;
`;

const QuantityRow = styled(Row)`
    height: 24px;
`;

export default OrderItem;
